import { addDays, key, parse } from './dates';
import { runPerfect, runTrack } from './streaks';
import type { Cfg, LedgerEntry, State } from './types';

/* Pocket money depends on whether the debt is still being paid off. */
export const baseFor = (cfg: Cfg, now: Date): number => {
  const from = parse(cfg.debtStart);
  const weeksElapsed = Math.max(0, Math.floor((now.getTime() - from.getTime()) / 604800000));
  const paid = Math.min(cfg.debtTotal, weeksElapsed * cfg.debtWeekly);
  return cfg.debtTotal - paid > 0 ? cfg.base : cfg.baseAfterDebt;
};

/* Streak payouts that closed inside the given week. */
export const earnedInWeek = (state: State, weekStart: Date, weekEnd: Date, now: Date): number => {
  const from = parse(state.cfg.debtStart);
  const horizon = now > weekEnd ? now : weekEnd;
  const home = runTrack(state.days, 'home', state.cfg.targetHome, state.cfg.rate, from, horizon);
  const school = runTrack(state.days, 'school', state.cfg.targetSchool, state.cfg.rate, from, horizon);
  const a = key(weekStart);
  const b = key(weekEnd);
  return [...home.awards, ...school.awards]
    .filter((x) => x.date >= a && x.date <= b)
    .reduce((n, x) => n + x.amount, 0);
};

/* How much allowance has already been moved into the account for this week. */
export const allowancePaid = (ledger: LedgerEntry[], weekKey: string): number =>
  ledger.filter((e) => e.kind === 'allowance' && e.week === weekKey).reduce((n, e) => n + e.amount, 0);

export const weekTotal = (state: State, weekStart: Date, weekEnd: Date, now: Date): number =>
  baseFor(state.cfg, now) + earnedInWeek(state, weekStart, weekEnd, now);

/* What is still waiting to be transferred. Paying settles this amount, so it
   drops to zero — but if more is earned afterwards, the remainder reappears
   and can be topped up. Never negative: a transfer is not clawed back. */
export const owedForWeek = (state: State, weekStart: Date, weekEnd: Date, now: Date): number =>
  Math.max(0, weekTotal(state, weekStart, weekEnd, now) - allowancePaid(state.ledger, key(weekStart)));

export const PERFECT_TARGET = 5;

/* The outing counter restarts the day after the reward was last claimed, so
   taking Inbar out does not leave the card stuck on "הגעת!" until a red day
   happens to break the run. */
export const perfectStreak = (state: State, from: Date, to: Date): number => {
  const last = [...state.outings].sort().pop();
  const since = last ? addDays(parse(last), 1) : from;
  return runPerfect(state.days, since > from ? since : from, to);
};

export const outingEarned = (state: State, from: Date, to: Date): boolean =>
  perfectStreak(state, from, to) >= PERFECT_TARGET;
